// "Your day" and "Make time" (/api/calendar/agenda and /api/calendar/focus*). The rail only reads;
// adding or removing a focus block creates a gateway approval that the executor runs, so nothing
// here writes to Google Calendar itself.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type {
  AgendaView,
  DayCalendarAdd,
  DayCalendarPatch,
  DayCalendarView,
  FocusRemoveRequest,
  FocusRemoveResult,
  FocusRequest,
  FocusResult,
  FocusSlotsRequest,
  FocusSlotsResponse,
  FocusSuggestResponse,
} from "@shared/agenda";
import { api } from "./api";

export const dayUrls = {
  agenda: () => "/api/calendar/agenda",
  focusSlots: () => "/api/calendar/focus/slots",
  focusSuggest: () => "/api/calendar/focus/suggest",
  focus: () => "/api/calendar/focus",
  focusRemove: () => "/api/calendar/focus/remove",
  dayCalendars: () => "/api/calendar/day-calendars",
  dayCalendar: (accountId: string, calendarId: string) =>
    `/api/calendar/day-calendars/${encodeURIComponent(accountId)}/${encodeURIComponent(calendarId)}`,
};

export const dayApi = {
  agenda: (signal?: AbortSignal) => api<AgendaView>(dayUrls.agenda(), { signal }),
  focusSlots: (body: FocusSlotsRequest, signal?: AbortSignal) =>
    api<FocusSlotsResponse>(dayUrls.focusSlots(), { method: "POST", body, signal }),
  /** One time tomorrow morning for a big card (U3); read-only. */
  focusSuggest: (body: FocusSlotsRequest, signal?: AbortSignal) =>
    api<FocusSuggestResponse>(dayUrls.focusSuggest(), { method: "POST", body, signal }),
  /** Approves one focus block (the dialog arms first; this is the confirm). */
  addFocus: (body: FocusRequest) => api<FocusResult>(dayUrls.focus(), { method: "POST", body }),
  /** Approves removing one focus block MeeMail made. */
  removeFocus: (body: FocusRemoveRequest) =>
    api<FocusRemoveResult>(dayUrls.focusRemove(), { method: "POST", body }),
  /** Which calendars "Your day" shows (MeeMail settings; nothing is written to Google). */
  dayCalendars: (signal?: AbortSignal) =>
    api<DayCalendarView[]>(dayUrls.dayCalendars(), { signal }),
  patchDayCalendar: (body: DayCalendarPatch) =>
    api<DayCalendarView[]>(dayUrls.dayCalendars(), { method: "PATCH", body }),
  /** The server reads the calendar once to check the id before it is listed. */
  addDayCalendar: (body: DayCalendarAdd) =>
    api<DayCalendarView[]>(dayUrls.dayCalendars(), { method: "POST", body }),
  removeDayCalendar: (accountId: string, calendarId: string) =>
    api<DayCalendarView[]>(dayUrls.dayCalendar(accountId, calendarId), { method: "DELETE" }),
};

export const dayKeys = {
  agenda: ["agenda"] as const,
  calendars: ["dayCalendars"] as const,
};

export const agendaQuery = queryOptions({
  queryKey: dayKeys.agenda,
  queryFn: ({ signal }) => dayApi.agenda(signal),
  // The server keeps Google's answer for two minutes; work and task events refresh it sooner.
  staleTime: 60_000,
  refetchInterval: 2 * 60_000,
});

export function useAgenda(enabled = true) {
  return useQuery({ ...agendaQuery, enabled });
}

export function useDayCalendars() {
  return useQuery({
    queryKey: dayKeys.calendars,
    queryFn: ({ signal }) => dayApi.dayCalendars(signal),
    staleTime: 30_000,
  });
}
